import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Calendar, MessageSquare, Users, Settings, 
  LogOut, Menu, X, Plus, Edit, Trash2, Eye, EyeOff, Check, Clock, Send, ArrowLeft, MessageCircle
} from 'lucide-react';
import Logo from '../components/layout/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../App';

// Dashboard Overview Component
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/admin/stats`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { title: 'Articles', value: stats?.articles?.total || 0, sub: `${stats?.articles?.published || 0} publiés`, color: 'bg-[#006F33]' },
    { title: 'Rendez-vous', value: stats?.appointments?.total || 0, sub: `${stats?.appointments?.pending || 0} en attente`, color: 'bg-[#E6C200]' },
    { title: 'Conversations', value: stats?.conversations?.total || 0, sub: `${stats?.conversations?.unread || 0} non lus`, color: 'bg-[#C0392B]' },
    { title: 'Utilisateurs', value: stats?.users?.total || 0, sub: 'inscrits', color: 'bg-slate-700' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-8">Tableau de bord</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card" data-testid={`stat-card-${index}`}>
            <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
              <span className="text-white font-bold text-lg">{stat.value}</span>
            </div>
            <h3 className="font-semibold text-[#1A1A1A]">{stat.title}</h3>
            <p className="text-sm text-slate-500">{stat.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Articles Management Component
const ArticlesManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', excerpt: '', category: '', image_url: '', published: false
  });

  const categories = ['Création d\'entreprise', 'Conseil juridique', 'Financement', 'Formation'];

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API}/articles?published_only=false`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingArticle 
        ? `${API}/admin/articles/${editingArticle.article_id}`
        : `${API}/admin/articles`;
      const method = editingArticle ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingArticle ? 'Article mis à jour' : 'Article créé');
        setIsDialogOpen(false);
        setEditingArticle(null);
        setFormData({ title: '', content: '', excerpt: '', category: '', image_url: '', published: false });
        fetchArticles();
      } else {
        throw new Error('Failed to save article');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (articleId) => {
    try {
      const response = await fetch(`${API}/admin/articles/${articleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Article supprimé');
        fetchArticles();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt,
      category: article.category,
      image_url: article.image_url || '',
      published: article.published
    });
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="articles-management">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Gestion des articles</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="btn-primary flex items-center gap-2"
              onClick={() => {
                setEditingArticle(null);
                setFormData({ title: '', content: '', excerpt: '', category: '', image_url: '', published: false });
              }}
              data-testid="new-article-btn"
            >
              <Plus className="w-5 h-5" />
              Nouvel article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="article-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="article-category-select">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Extrait *</Label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  rows={2}
                  data-testid="article-excerpt-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contenu *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={10}
                  data-testid="article-content-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL de l'image</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="article-image-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="article-published-checkbox"
                />
                <Label htmlFor="published">Publier l'article</Label>
              </div>
              <Button type="submit" className="btn-primary w-full" data-testid="article-submit-btn">
                {editingArticle ? 'Mettre à jour' : 'Créer l\'article'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner w-12 h-12"></div>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          Aucun article pour le moment.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left">Titre</th>
                <th className="px-6 py-4 text-left">Catégorie</th>
                <th className="px-6 py-4 text-left">Statut</th>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.article_id} className="table-row" data-testid={`article-row-${article.article_id}`}>
                  <td className="px-6 py-4 font-medium">{article.title}</td>
                  <td className="px-6 py-4 text-slate-600">{article.category}</td>
                  <td className="px-6 py-4">
                    {article.published ? (
                      <span className="badge-confirmed flex items-center gap-1 w-fit">
                        <Eye className="w-3 h-3" /> Publié
                      </span>
                    ) : (
                      <span className="badge-pending flex items-center gap-1 w-fit">
                        <EyeOff className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-500">
                    {new Date(article.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(article)}
                        data-testid={`edit-article-${article.article_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600" data-testid={`delete-article-${article.article_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer l'article ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(article.article_id)} className="bg-red-600">
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Appointments Management Component
const AppointmentsManagement = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAppointments = async () => {
    try {
      const response = await fetch(`${API}/admin/appointments`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const updateStatus = async (appointmentId, status) => {
    try {
      const response = await fetch(`${API}/admin/appointments/${appointmentId}?status=${status}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchAppointments();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="badge-confirmed">Confirmé</span>;
      case 'cancelled':
        return <span className="badge-cancelled">Annulé</span>;
      default:
        return <span className="badge-pending">En attente</span>;
    }
  };

  return (
    <div data-testid="appointments-management">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-8">Gestion des rendez-vous</h1>
      
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner w-12 h-12"></div>
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          Aucun rendez-vous pour le moment.
        </div>
      ) : (
        <div className="space-y-4">
          {appointments.map((apt) => (
            <div key={apt.appointment_id} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`appointment-${apt.appointment_id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#1A1A1A]">{apt.client_name}</h3>
                    {statusBadge(apt.status)}
                  </div>
                  <p className="text-slate-600 text-sm">{apt.client_email} • {apt.client_phone}</p>
                  <p className="text-slate-500 text-sm mt-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {apt.date} à {apt.time} • {apt.subject}
                  </p>
                  {apt.message && (
                    <p className="text-slate-500 text-sm mt-2 italic">"{apt.message}"</p>
                  )}
                </div>
                
                {apt.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-[#006F33] hover:bg-[#004D24] text-white"
                      onClick={() => updateStatus(apt.appointment_id, 'confirmed')}
                      data-testid={`confirm-apt-${apt.appointment_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Confirmer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-600"
                      onClick={() => updateStatus(apt.appointment_id, 'cancelled')}
                      data-testid={`cancel-apt-${apt.appointment_id}`}
                    >
                      Annuler
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Messages Management Component - Interactive Conversations
const MessagesManagement = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/admin/conversations`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await fetch(`${API}/admin/conversations/${conversationId}`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
        setMessages(data.messages);
        // Refresh conversations to update unread count
        fetchConversations();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API}/admin/conversations/${selectedConversation.conversation_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reply_text: replyText })
      });
      
      if (response.ok) {
        setReplyText('');
        fetchMessages(selectedConversation.conversation_id);
        toast.success('Réponse envoyée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      await fetch(`${API}/admin/conversations/${selectedConversation.conversation_id}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchConversations();
      setSelectedConversation({ ...selectedConversation, status });
      toast.success(status === 'closed' ? 'Conversation fermée' : 'Conversation rouverte');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div data-testid="messages-management" className="h-[calc(100vh-120px)]">
      <div className="flex h-full gap-6">
        {/* Conversations List */}
        <div className={`${selectedConversation ? 'hidden md:block' : ''} w-full md:w-1/3 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col`}>
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-bold text-[#1A1A1A] flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#006F33]" />
              Conversations
            </h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              Aucune conversation
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => fetchMessages(conv.conversation_id)}
                  className={`p-4 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id ? 'bg-[#006F33]/5 border-l-4 border-l-[#006F33]' : ''
                  }`}
                  data-testid={`conv-${conv.conversation_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#1A1A1A] truncate">{conv.client_name}</h3>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#C0392B] text-white text-xs px-2 py-0.5 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#006F33] truncate">{conv.subject}</p>
                      <p className="text-xs text-slate-400 mt-1">{formatTime(conv.updated_at)}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      conv.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {conv.status === 'open' ? 'Ouvert' : 'Fermé'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Messages Area */}
        <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white rounded-xl shadow-sm overflow-hidden`}>
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h3 className="font-bold text-[#1A1A1A]">{selectedConversation.client_name}</h3>
                    <p className="text-sm text-slate-500">{selectedConversation.client_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConversation.client_phone && (
                    <a 
                      href={`tel:${selectedConversation.client_phone}`}
                      className="text-sm text-[#006F33] hover:underline"
                    >
                      {selectedConversation.client_phone}
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(selectedConversation.status === 'open' ? 'closed' : 'open')}
                  >
                    {selectedConversation.status === 'open' ? 'Fermer' : 'Rouvrir'}
                  </Button>
                </div>
              </div>

              {/* Subject Banner */}
              <div className="px-4 py-2 bg-[#006F33]/5 border-b border-slate-100">
                <p className="text-sm"><span className="font-medium">Sujet :</span> {selectedConversation.subject}</p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingMessages ? (
                  <div className="flex justify-center py-10">
                    <div className="spinner w-8 h-8"></div>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                        msg.sender_type === 'admin' 
                          ? 'bg-[#006F33] text-white rounded-br-sm' 
                          : 'bg-slate-100 text-[#1A1A1A] rounded-bl-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-white/70' : 'text-slate-400'}`}>
                          {msg.sender_name} • {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-slate-100">
                <div className="flex gap-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Écrivez votre réponse..."
                    className="flex-1 resize-none"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    data-testid="reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="btn-primary self-end"
                    data-testid="send-reply-btn"
                  >
                    {sending ? (
                      <div className="spinner w-5 h-5 border-white"></div>
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Appuyez sur Entrée pour envoyer</p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Sélectionnez une conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Page
const DashboardPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/dashboard/articles', icon: FileText, label: 'Articles' },
    { path: '/dashboard/appointments', icon: Calendar, label: 'Rendez-vous' },
    { path: '/dashboard/messages', icon: MessageSquare, label: 'Messages' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Check if user is admin
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Accès refusé</h1>
          <p className="text-slate-600 mb-8">Vous n'avez pas les droits d'administration.</p>
          <Link to="/">
            <Button className="btn-primary">Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F9F9F7]" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className={`admin-sidebar w-64 fixed lg:static inset-y-0 left-0 z-50 transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 transition-transform duration-200`}>
        <div className="p-6">
          <Link to="/" className="block">
            <Logo variant="light" size="small" />
          </Link>
        </div>
        
        <nav className="px-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-sidebar-link ${
                location.pathname === item.path ? 'active' : ''
              }`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <button
            onClick={handleLogout}
            className="admin-sidebar-link w-full text-red-400 hover:text-red-300"
            data-testid="dashboard-logout"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between lg:justify-end">
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Bonjour, {user?.name?.split(' ')[0]}</span>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#006F33] flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="articles" element={<ArticlesManagement />} />
            <Route path="appointments" element={<AppointmentsManagement />} />
            <Route path="messages" element={<MessagesManagement />} />
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardPage;
